import { useEffect, useMemo, useRef, useState } from 'react';
import { CardRecord, SpeakingMeta, SpeakingContent, updateCard } from '../db/indexedDb';

type Props = {
  card: CardRecord;
};

type RecorderState = 'idle' | 'recording';

/** 获取浏览器支持的音频 MIME 类型 */
const getSupportedMimeType = (): string => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/wav',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return ''; // 使用浏览器默认
};

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const pickRecording = (meta?: SpeakingMeta, card?: CardRecord) => {
  const fromExtra = meta?.extraMeta?.lightReviewRecordingKey as string | undefined;
  return fromExtra ?? meta?.finalRecordingKey ?? card?.audioBlobRef ?? meta?.draftRecordingKey ?? card?.audioUrl ?? null;
};

function mergeLightReviewMeta(current: SpeakingMeta | undefined, dataUrl: string): SpeakingMeta {
  const extraMeta = { ...(current?.extraMeta ?? {}), lightReviewRecordingKey: dataUrl };
  return { ...(current ?? {}), extraMeta };
}

function SpeakingLightReviewCard({ card }: Props) {
  const baseMeta = card.speakingMeta ?? {};
  const [meta, setMeta] = useState<SpeakingMeta>(baseMeta);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(() => pickRecording(baseMeta, card));
  const [messages, setMessages] = useState<string[]>([]);
  const [recorderState, setRecorderState] = useState<RecorderState>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const speakingContent = (card.content ?? {}) as SpeakingContent & { cues?: string[] };
  const question = speakingContent.question ?? meta.question ?? '口语题目';
  const topic = speakingContent.topic ?? meta.topic ?? '主题';
  const cues = useMemo(() => (Array.isArray(speakingContent.cues) ? speakingContent.cues : []), [speakingContent]);

  const transcript = meta.transcript ?? '暂无转写，直接回顾题干即可。';
  const selfReview = meta.selfReview ?? '上次未填写自评，可根据录音自行对照。';
  const summary = meta.bestAnswerSummary ?? '暂无最佳答案摘要，专注于熟悉表达节奏。';

  const addMessage = (text: string) => setMessages((prev) => [...prev.slice(-3), text]);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => cleanupStream(), []);

  const persistRecording = async (dataUrl: string) => {
    const nextMeta = mergeLightReviewMeta(meta, dataUrl);
    setMeta(nextMeta);

    const updates: Partial<CardRecord> = { speakingMeta: nextMeta };
    if (!card.audioBlobRef) updates.audioBlobRef = dataUrl;
    await updateCard(card.id, updates);
  };

  const handleRecordingStop = async (blob: Blob) => {
    setRecorderState('idle');
    cleanupStream();
    const dataUrl = await blobToDataUrl(blob);
    setPlaybackUrl(dataUrl);
    await persistRecording(dataUrl);
    addMessage('已保存本地录音，离线也可回放。');
  };

  const startRecording = async () => {
    if (recorderState === 'recording') return;
    if (!navigator.mediaDevices?.getUserMedia) {
      addMessage('当前设备不支持浏览器录音，已降级为纯文本回顾。');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      const recorderOptions = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(stream, recorderOptions);
      const actualMimeType = recorder.mimeType || mimeType || 'audio/webm';
      recorderRef.current = recorder;
      streamRef.current = stream;
      chunksRef.current = [];

      recorder.ondataavailable = (evt) => {
        if (evt.data.size > 0) chunksRef.current.push(evt.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        await handleRecordingStop(blob);
      };

      recorder.start();
      setRecorderState('recording');
      addMessage('录音中（本地存储，不会调用云端）。点击"结束本地重录"保存。');
    } catch (err) {
      addMessage(`无法启动录音：${(err as Error).message}`);
      cleanupStream();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderState === 'recording') {
      recorderRef.current.stop();
    }
  };


  return (
    <div className="session-card speaking-card light-review-card">
      <div className="speaking-head">
        <div>
          <p className="eyebrow">口语 · 轻复习（离线 · 无 AI 依赖）</p>
          <h3>{question}</h3>
          <p className="session-meta">{topic} · 使用上一次录音/转写快速回顾</p>
        </div>
        <div className="mode-pill offline">
          <span>轻复习 · 本地数据</span>
          <small>不调用 STT/LLM · Again/Hard/Good 写回 SRS</small>
        </div>
      </div>

      {cues.length ? (
        <ul className="session-list">
          {cues.map((cue) => (
            <li key={cue}>{cue}</li>
          ))}
        </ul>
      ) : null}

      <div className="speaking-section">
        <div className="section-label">上次转写 / 自评</div>
        <div className="light-review-grid">
          <div>
            <p className="eyebrow">上次转写</p>
            <p className="session-body">{transcript}</p>
          </div>
          <div>
            <p className="eyebrow">自评</p>
            <p className="session-body">{selfReview}</p>
          </div>
          <div>
            <p className="eyebrow">最佳答案摘要</p>
            <p className="session-body">{summary}</p>
          </div>
        </div>
        <p className="session-meta">以上内容全部来自本地缓存，不会触发任何网络请求。</p>
      </div>

      <div className="speaking-section">
        <div className="section-label">录音回顾（可选）</div>
        {playbackUrl ? <audio controls src={playbackUrl} /> : <p className="session-meta">暂无录音，直接评分或重新录一段也可以。</p>}
        <div className="speaking-actions">
          <button type="button" onClick={startRecording} disabled={recorderState === 'recording'}>
            {recorderState === 'recording' ? '录音中…' : '快速重录（本地）'}
          </button>
          <button type="button" className="ghost" onClick={stopRecording} disabled={recorderState !== 'recording'}>
            结束本地重录
          </button>
          <span className="pill micro subtle">仅保存到 IndexedDB · 离线可用</span>
        </div>
      </div>

      <div className="hint-box">
        <p className="eyebrow">轻量提示</p>
        <p className="session-body">没有联网或未配置 Key 也能完成本次回顾，直接使用下方 Again/Hard/Good 打分即可。</p>
      </div>

      {messages.length ? (
        <div className="message-list">
          {messages.map((text, idx) => (
            <p key={`${text}-${idx}`} className="message info">
              {text}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default SpeakingLightReviewCard;
